import React, { useMemo, useState } from 'react';
import StbButton from '../security-toolbox-ui/StbButton';
import { useDeleteLabRow, useLabExecutionMethods } from '../../hooks/lab-hooks';
import {
  LabExecutionMethod,
  labActionTypeLabel,
  labMethodBlockers,
  labTransportLabel,
  LAB_TABLES,
} from '../../support/labService';
import LabExecutionMethodEditorModal from './LabExecutionMethodEditorModal';
import { LabCard, LabJsonView, LabPanelState, LabStatusTag, labErrorMessage } from './LabShared';

export type LabExecutionMethodsPanelProps = {
  /** Authoring. The API refuses either way; this decides whether the buttons are there. */
  canEdit?: boolean;
  selectedId?: number | null;
  onSelect?: (method: LabExecutionMethod) => void;
};

/**
 * How work is carried out, as a table.
 *
 * REST, SSH, PowerShell, Ansible, Terraform - and the sixth one, which is why this is a table and
 * not a list in the code. What keeps it from being a way to smuggle new behaviour into a pod is the
 * action column: every method resolves to one of the twelve actions the agent already implements,
 * so a row here describes work rather than introducing a runner. Adding one needs no release.
 *
 * The column worth reading is "can check". A method that only applies reports its own exit code, so
 * scoring a lab against it scores the tool having run - which passes whether or not the pod ended up
 * in the state the item claims.
 */
const LabExecutionMethodsPanel: React.FC<LabExecutionMethodsPanelProps> = ({
  canEdit = false,
  selectedId,
  onSelect,
}) => {
  const [editor, setEditor] = useState<{ open: boolean; method: LabExecutionMethod | null }>({
    open: false,
    method: null,
  });
  const [removeError, setRemoveError] = useState<string | null>(null);

  const { data: catalogue, isLoading, error } = useLabExecutionMethods();
  const deleteMethod = useDeleteLabRow(LAB_TABLES.executionMethod);

  const rows = useMemo(() => catalogue || [], [catalogue]);

  const remove = async (method: LabExecutionMethod) => {
    const used = method.item_count ?? 0;

    // eslint-disable-next-line no-restricted-globals
    if (
      !window.confirm(
        used > 0
          ? `${method.name} is used by ${used} ${
              used === 1 ? 'item' : 'items'
            }. Deleting it will be refused while that is true - the items protect it. Disable it instead to retire it without breaking them. Try anyway?`
          : `Delete ${method.name}? Nothing refers to it.`,
      )
    ) {
      return;
    }

    setRemoveError(null);

    try {
      await deleteMethod.mutateAsync(method.id);
    } catch (exc) {
      setRemoveError(
        labErrorMessage(
          exc,
          'Could not delete the method. Items that name it hold it in place - disable it instead, and their steps stop being generated for that role.',
        ),
      );
    }
  };

  return (
    <>
      <LabCard
        title={`Execution methods (${rows.length})`}
        subtitle="How a piece of work is carried out. Each one resolves to an action the agent already implements, which is what makes adding a method a data change rather than a release."
        actions={
          canEdit ? (
            <StbButton
              role="add"
              size="small"
              onClick={async () => setEditor({ open: true, method: null })}
            >
              New method
            </StbButton>
          ) : undefined
        }
      >
        {removeError && <p className="m-0 mb-3 text-sm text-amber-800">{removeError}</p>}

        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!rows.length}
          emptyMessage="No methods yet, so an item has no way to say how its work is done and every spec falls back to a plain shell command. Run seed_lab_reference_data, or author them here."
          loadingLabel="Loading methods..."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Method</th>
                  <th className="px-3 py-2 font-semibold">Becomes</th>
                  <th className="px-3 py-2 font-semibold">Can check</th>
                  <th className="px-3 py-2 font-semibold">Templates</th>
                  <th className="px-3 py-2 font-semibold">Used by</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((method) => {
                  const blockers = labMethodBlockers(method);
                  const selected = selectedId === method.id;

                  return (
                    <tr
                      key={method.id}
                      className={`border-b border-gray-100 align-top ${selected ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{method.name}</div>
                        <div className="font-mono text-xs text-gray-500">{method.key}</div>
                        {method.description && (
                          <div className="mt-1 text-xs text-gray-500">{method.description}</div>
                        )}

                        <div className="mt-1 flex flex-wrap gap-1">
                          {!method.enabled && <LabStatusTag label="Disabled" sentiment="warning" />}
                          {selected && <LabStatusTag label="Selected" sentiment="info" />}
                        </div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div>{method.action_type_label || labActionTypeLabel(method.action_type)}</div>
                        <div className="text-xs text-gray-500">
                          {method.transport
                            ? labTransportLabel(method.transport)
                            : "the device's transport"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {method.default_timeout_seconds}s default
                        </div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        {method.supports_check ? (
                          <LabStatusTag label="Yes" sentiment="success" />
                        ) : (
                          <LabStatusTag label="Applies only" sentiment="warning" />
                        )}
                        {/* Spelled out rather than left as a tag: this is the one that scores a
                            lab as passed on the strength of a tool exiting zero. */}
                        {!method.supports_check && (
                          <div className="mt-1 text-xs text-amber-800">
                            Honest as an action method only.
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        {Object.keys(method.payload_template || {}).length ? (
                          <LabJsonView value={method.payload_template} className="max-h-24" />
                        ) : (
                          <span className="text-xs text-gray-500">No payload template</span>
                        )}
                        {!!Object.keys(method.expect_template || {}).length && (
                          <div className="mt-1">
                            <LabJsonView value={method.expect_template} className="max-h-24" />
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div>{method.item_count ?? 0}</div>
                        <div className="text-xs text-gray-500">
                          {(method.item_count ?? 0) === 1 ? 'item' : 'items'}
                        </div>
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {onSelect && !selected && (
                            <StbButton
                              role="default"
                              size="xsmall"
                              onClick={async () => onSelect(method)}
                            >
                              Select
                            </StbButton>
                          )}
                          {canEdit && (
                            <>
                              <StbButton
                                role="update"
                                size="xsmall"
                                onClick={async () => setEditor({ open: true, method })}
                              >
                                Edit
                              </StbButton>
                              <StbButton
                                role="delete"
                                size="xsmall"
                                loading={deleteMethod.isPending}
                                onClick={async () => remove(method)}
                              >
                                Delete
                              </StbButton>
                            </>
                          )}
                        </div>

                        {blockers.length > 1 && (
                          <ul className="mb-0 mt-1 list-disc pl-4 text-left text-xs text-amber-800">
                            {blockers.map((blocker) => (
                              <li key={blocker}>{blocker}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </LabPanelState>
      </LabCard>

      <LabExecutionMethodEditorModal
        open={editor.open}
        method={editor.method}
        onClose={() => setEditor({ open: false, method: null })}
        onSaved={(saved) => onSelect?.(saved)}
      />
    </>
  );
};

export default LabExecutionMethodsPanel;
